import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import TransactionalMessageModel, {
  TransactionalMessageUpdateType,
  SettingsKey,
} from 'cio/models/transactional-message';
import TransactionalSetupBaseController from 'cio/pods/transactional/message/setup/base-controller';
import RouterService from '@ember/routing/router-service';
import AnalyticsService from 'cio/services/analytics';
import { tracked } from '@glimmer/tracking';

export default class TransactionalMessageSetupSettingsController extends TransactionalSetupBaseController {
  @service router!: RouterService;
  @service analytics!: AnalyticsService;

  declare model: TransactionalMessageModel;

  @computed('model.hasDirtyAttributes')
  get isDirty() {
    return this.model.hasDirtyAttributes;
  }

  @action
  updateSetting(attr: SettingsKey, value: boolean) {
    this.model.set(attr, value);
  }

  originalTriggerName!: string;
  @tracked triggerNameValidationErrors: string[] = [];

  saveTask = dropTask(async () => {
    this.triggerNameValidationErrors = [];
    this.model.set('updateType', TransactionalMessageUpdateType.Settings);

    try {
      await this.model.save();
      this.router.transitionTo('transactional.message.setup.review', this.model);

      if (this.originalTriggerName !== this.model.triggerName) {
        this.analytics.track('transactional', 'edit_started', { editType: 'triggerName', model: this.model });
        this.originalTriggerName = this.model.triggerName;
      }
    } catch (e) {
      let [error] = e.errors;
      let detail = (error?.detail ?? '') as string;

      if (error?.status === '400' && detail.toLowerCase().includes('trigger name')) {
        this.triggerNameValidationErrors = [detail];
      } else {
        throw e;
      }
    }
  });

  resetTask = dropTask(async () => {
    this.model.rollbackAttributes();
  });
}

declare module '@ember/controller' {
  interface Registry {
    'transactional-message-setup-settings': TransactionalMessageSetupSettingsController;
  }
}
